import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { UndoToast } from '../components/molecules/undo-toast/undo-toast';
import type { DeferredActionOptions } from '../types/deferred-action.types';

import { DeferredActionContext } from './deferred-action-context';

/** Keeps undo windows alive across route changes; leaving the app cancels pending work. */
export const DeferredActionProvider = ({ children }: PropsWithChildren) => {
  const versions = useRef(new Map<string, symbol>());
  const nextEntryId = useRef(0);
  const entries = useRef(
    new Map<
      string,
      {
        id: number;
        options: DeferredActionOptions;
        deadline: number;
        duration: number;
        timer: ReturnType<typeof setTimeout>;
        resolve: (commit: boolean) => void;
      }
    >()
  );
  const [revision, setRevision] = useState(0);
  const [now, setNow] = useState(Date.now());
  const finish = useCallback((key: string, commit: boolean) => {
    const entry = entries.current.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    entries.current.delete(key);
    entry.resolve(commit);
    setRevision((value) => value + 1);
  }, []);
  const cancel = useCallback(
    (key: string) => {
      versions.current.delete(key);
      for (const [entryKey, entry] of entries.current) {
        if (entryKey === key || entry.options.resourceKeys?.includes(key)) {
          for (const resource of entry.options.resourceKeys ?? []) {
            versions.current.delete(resource);
          }
          finish(entryKey, false);
        }
      }
    },
    [finish]
  );
  const start = useCallback(
    (key: string) => {
      cancel(key);
      const token = Symbol(key);
      versions.current.set(key, token);
      return {
        isCurrent: () => versions.current.get(key) === token,
        release: () => {
          if (versions.current.get(key) === token) {
            versions.current.delete(key);
          }
        },
      };
    },
    [cancel]
  );
  const wait = useCallback(
    (options: DeferredActionOptions) => {
      finish(options.key, false);
      const duration = options.durationMs ?? 5000;
      if (!Number.isFinite(duration) || duration <= 0) {
        return Promise.resolve(false);
      }
      return new Promise<boolean>((resolve) => {
        entries.current.set(options.key, {
          id: ++nextEntryId.current,
          options,
          duration,
          deadline: Date.now() + duration,
          resolve,
          timer: setTimeout(() => finish(options.key, true), duration),
        });
        setNow(Date.now());
        setRevision((value) => value + 1);
      });
    },
    [finish]
  );
  useEffect(() => {
    const pending = entries.current;
    const cancelAll = () => {
      for (const entry of pending.values()) {
        clearTimeout(entry.timer);
        entry.resolve(false);
      }
      pending.clear();
      versions.current.clear();
      setRevision((value) => value + 1);
    };
    window.addEventListener('pagehide', cancelAll);
    return () => {
      window.removeEventListener('pagehide', cancelAll);
      cancelAll();
    };
  }, []);
  useEffect(() => {
    if (!entries.current.size) return;
    const timer = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(timer);
  }, [revision]);
  const value = useMemo(() => ({ wait, cancel, start }), [wait, cancel, start]);
  return (
    <DeferredActionContext.Provider value={value}>
      {children}
      <div className="fixed bottom-6 left-4 right-4 z-[10001] flex max-h-[50vh] flex-col gap-2 overflow-y-auto md:left-6 md:right-auto md:w-96">
        {[...entries.current].map(([key, entry]) => (
          <UndoToast
            key={entry.id}
            focusUndo={entry.options.focusUndo}
            message={entry.options.message}
            undoLabel={entry.options.undoLabel}
            remaining={(entry.deadline - now) / entry.duration}
            onUndo={() => cancel(key)}
          />
        ))}
      </div>
    </DeferredActionContext.Provider>
  );
};
